import React, { useMemo, useState } from "react";

import { useAuth } from "../../../../hooks/auth";
import { useCompanies } from "../../../../hooks/companies";
import NotaFiscalDetailModal from "../../ConsultaNotasFiscais/NotaFiscalDetailModal";
import LoteDetalheModal from "../../DespachoLotes/LoteDetalheModal";
import PedidoDetalheModal from "../../Pedido/PedidoDetalheModal";
import { temPermissao } from "../../AuditoriaPedido/regras";

import CartaoGrafico from "./CartaoGrafico";
import QuadroPedidos from "./QuadroPedidos";
import { linkIndicadores } from "./IndicadoresDespacho";
import useQuadroDespacho, { LIMITE_QUADRO_TORRE } from "./useQuadroDespacho";

// Quadro do despacho na Torre (aba Despacho, largura total abaixo dos KPIs) e
// no Meu Painel (widget `despacho-quadro`) — PO1-3500, épico PO1-3493.
//
// Mesmo contrato dos outros 6 blocos da aba (`deferLoad` / `onRegisterFetch`,
// casca CartaoGrafico, período do filtro global com padrão de 30 dias), mas
// com a própria chamada: 1 /despacho/indicadores/quadro, sem tocar no /resumo.
//
// Limite 10 por coluna (e não 20): a Torre é um panorama com outros 6 blocos;
// cada coluna mostra ~4 cartões e rola o resto, então 10 cobre os mais parados
// com metade da resposta (70 cartões em vez de 140). Para ver tudo, "Ver todos"
// abre a lista de Trackfy › Indicadores › Despacho já na etapa e no período.

// O detalhe da NF-e (GET /notas-fiscais/consulta/:id) pede uma destas: sem
// nenhuma, o cartão não mostra o atalho NF-e (o modal só daria 403).
const PERMISSOES_NFE = ["Pages.HUB.Monitoramento_NotasFiscais", "Pages.HUB.Pedidos"];
// Modal do pedido: GET /trackfy/pedidos/notas e /pedidos/detalhe. O do lote usa
// a permissão de despacho que o próprio quadro já exige; ações só com editar.
const PERMISSOES_PEDIDO = ["hub.operacao.pedidos.visualizar", "Pages.HUB.Pedidos"];
const PERMISSAO_DESPACHO_EDITAR = "hub.operacao.despacho.editar";

export default function QuadroDespacho({ deferLoad = false, onRegisterFetch }) {
  const { dados, carregando, carregado, erro, periodo, recarregar } = useQuadroDespacho({ deferLoad, onRegisterFetch, limite: LIMITE_QUADRO_TORRE });
  const verTodos = (etapa) => linkIndicadores(periodo, etapa);
  const { user } = useAuth();
  const { empresaId, transportadoraId, arrayParams } = useCompanies();
  const [notaAberta, setNotaAberta] = useState(null);
  const [pedidoAberto, setPedidoAberto] = useState(null);
  const [loteAberto, setLoteAberto] = useState(null);
  const podeVerNota = PERMISSOES_NFE.some((p) => temPermissao(user, p));
  const podeVerPedido = PERMISSOES_PEDIDO.some((p) => temPermissao(user, p));
  const podeEditarLote = temPermissao(user, PERMISSAO_DESPACHO_EDITAR);
  // Mesmo recorte do quadro para as notas do pedido.
  const transportadorasFiltro = arrayParams && arrayParams.transportadoras;
  const chaveTransportadoras = (transportadorasFiltro || []).join(",");
  const recortePedido = useMemo(
    () => ({ ...periodo, ...(transportadorasFiltro && transportadorasFiltro.length ? { transportadoras: transportadorasFiltro } : {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodo.de, periodo.ate, chaveTransportadoras]
  );

  return (
    <div className="despacho-quadro-bloco" data-testid="quadro-despacho">
      <CartaoGrafico
        titulo="Quadro do despacho"
        subtitulo={`As colunas contam pedidos, não envios. Cada coluna mostra os ${dados.limite || LIMITE_QUADRO_TORRE} mais parados.`}
        carregando={carregando || !carregado}
        erro={erro}
        textoErro="Não conseguimos carregar o quadro do despacho."
      >
        <QuadroPedidos
          colunas={dados.colunas}
          linkVerTodos={verTodos}
          onAbrirNota={podeVerNota ? setNotaAberta : undefined}
          onAbrirPedido={podeVerPedido ? setPedidoAberto : undefined}
          onAbrirLote={setLoteAberto}
        />
      </CartaoGrafico>
      <NotaFiscalDetailModal
        isOpen={Boolean(notaAberta)}
        notaFiscalId={notaAberta}
        embarcadorId={empresaId}
        transportadoraId={transportadoraId}
        onClose={() => setNotaAberta(null)}
      />
      {pedidoAberto ? (
        <PedidoDetalheModal
          pedido={pedidoAberto}
          paramsRecorte={recortePedido}
          embarcadorId={empresaId}
          transportadoraId={transportadoraId}
          onClose={() => setPedidoAberto(null)}
        />
      ) : null}
      {loteAberto ? (
        <LoteDetalheModal
          idLote={loteAberto}
          podeEditar={podeEditarLote}
          onClose={(alterou) => {
            setLoteAberto(null);
            // Fechou, despachou ou pediu coleta no modal: o cartão muda de coluna.
            if (alterou) recarregar();
          }}
        />
      ) : null}
    </div>
  );
}
